/**
 * Kształt chmur, jako jeden kawałek GLSL-a.
 *
 * **Jedna funkcja dla chmury i dla jej cienia.** Warstwa nad światem i
 * przyciemnienie gruntu pod nią muszą pytać dokładnie tego samego pola szumu,
 * inaczej cień nie należy do chmury, którą widać — a to jest jedyna rzecz, na
 * której cała ta iluzja stoi. Dlatego kod jest tekstem wstawianym w oba
 * shadery, a nie dwiema implementacjami, które ktoś kiedyś zsynchronizuje.
 *
 * **Pole jest trójwymiarowe, odkąd chmurę się marszuje.** Warstwa nie jest już
 * płaszczyzną z namalowanym szumem, tylko **płytą o grubości**, przez którą
 * promień kamery przechodzi kilkunastoma krokami (patrz `Clouds.ts`). Cień
 * bierze z tej samej płyty **jedną próbkę w jej środku** — całkowanie po pionie
 * dałoby to samo z dokładnością, której na przyciemnieniu gruntu nikt nie
 * zobaczy, a kosztowałoby tyle samo, co cała chmura, na każdym pikselu terenu.
 *
 * Szum jest proceduralny, bo tekstury chmur nie mamy i nie musimy jej mieć.
 */

/** Ile jednostek świata nad zerem leży **środek** warstwy chmur. */
export const CLOUD_HEIGHT = 34;

/**
 * Jak gruba jest płyta, w jednostkach świata.
 *
 * **Grubość jest tym, co odróżnia chmurę od namalowanej plamy** — promień musi
 * mieć przez co przejść, żeby gdziekolwiek zebrać więcej gęstości niż gdzie
 * indziej. Sześć jednostek to dwanaście heksów: dość, by przy izometrycznym
 * kącie patrzenia droga promienia przez płytę zmieniała się widocznie między
 * środkiem kłębu a jego brzegiem.
 */
export const CLOUD_THICKNESS = 6;

/**
 * Jak duże są kłęby: mnożnik współrzędnych świata w argumencie szumu.
 *
 * Heks ma 0,5 promienia, więc 0,02 daje chmurę na kilkadziesiąt pól — coś, co
 * przechodzi nad graczem w kilkanaście sekund, a nie łatka wielkości pionka.
 */
export const CLOUD_SCALE = 0.02;

/**
 * The shared field, in GLSL.
 *
 * `cloudDensity` answers "how much cloud is at this point of the world", from 0
 * to 1. `cloudAt` is the flat question the ground shaders ask — the same field,
 * sampled in the middle of the slab.
 *
 * The threshold moves rather than the noise, so thickening weather grows the
 * clouds that are there instead of sliding a different sky in.
 */
export const CLOUD_GLSL = /* glsl */ `
  float cloudHash(vec3 p) {
    return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453123);
  }

  float cloudValue(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    // Smoothstep on the cell fraction: linear interpolation would leave the
    // grid of the noise visible as diamonds.
    vec3 u = f * f * (3.0 - 2.0 * f);

    float x00 = mix(cloudHash(i + vec3(0.0, 0.0, 0.0)), cloudHash(i + vec3(1.0, 0.0, 0.0)), u.x);
    float x10 = mix(cloudHash(i + vec3(0.0, 1.0, 0.0)), cloudHash(i + vec3(1.0, 1.0, 0.0)), u.x);
    float x01 = mix(cloudHash(i + vec3(0.0, 0.0, 1.0)), cloudHash(i + vec3(1.0, 0.0, 1.0)), u.x);
    float x11 = mix(cloudHash(i + vec3(0.0, 1.0, 1.0)), cloudHash(i + vec3(1.0, 1.0, 1.0)), u.x);

    return mix(mix(x00, x10, u.y), mix(x01, x11, u.y), u.z);
  }

  // Dwie oktawy, nie cztery, i to jest cena marszu: kształt pytany dwanaście
  // razy na piksel musi być tańszy niż kształt pytany raz. Trzecia oktawa dodaje
  // detal na jakieś pół piksela bufora — czyli migotanie, nie puszystość.
  float cloudFbm(vec3 p) {
    float sum = 0.0;
    float amplitude = 0.5;
    for (int octave = 0; octave < 2; octave++) {
      sum += amplitude * cloudValue(p);
      p = p * 2.03 + vec3(17.3, 9.1, 5.7);
      amplitude *= 0.5;
    }
    return sum * 1.33;
  }

  /**
   * Ile chmury jest w tym punkcie świata.
   *
   * Pion wchodzi **dwa razy i w dwóch różnych rolach**: raz jako współrzędna
   * szumu, żeby kłąb miał kształt także w górę, i raz jako profil płyty, który
   * wygasza gęstość przy jej powierzchniach. Bez profilu chmura byłaby prostopadłościanem
   * z postrzępionym wnętrzem i pokazywałaby przy obrocie swoje płaskie dno.
   */
  float cloudDensity(vec3 world, vec2 offset, float scale, float cover) {
    vec3 p = vec3(world.x + offset.x, world.y, world.z + offset.y) * scale;
    // Pion rozciągnięty: kłęby są szersze niż wyższe, a szum pytany tą samą
    // skalą w trzech osiach dałby kule.
    p.y *= 3.0;

    float density = cloudFbm(p);

    // half jest w GLSL słowem zarezerwowanym — stąd ta nazwa.
    float halfSlab = ${CLOUD_THICKNESS}.0 * 0.5;
    float fromMiddle = abs(world.y - ${CLOUD_HEIGHT}.0) / halfSlab;
    float slab = 1.0 - smoothstep(0.35, 1.0, fromMiddle);

    // Clear sky sits above anything the fbm produces, so cover 0 is genuinely
    // nothing rather than a faint haze; overcast sits below nearly all of it.
    float edge = mix(0.82, 0.18, cover);
    return smoothstep(edge, edge + 0.20, density) * slab;
  }

  /**
   * Płaskie pytanie, dla cienia na ziemi: ile chmury wisi nad tym punktem.
   *
   * Próbka ze **środka płyty**, gdzie profil jest pełny — więc odpowiedź jest
   * tą samą gęstością, którą zbiera promień przechodzący przez ten sam kłąb.
   */
  float cloudAt(vec2 world, vec2 offset, float scale, float cover) {
    return cloudDensity(vec3(world.x, ${CLOUD_HEIGHT}.0, world.y), offset, scale, cover);
  }
`;
